import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { collect } from 'collect.js';
import i18n from 'i18next';
import { useSelector } from 'react-redux';
import { Button, Card, Empty, Flex, Skeleton } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import useApiClient from '../../../hooks/api/useApiClient';
import useForm from '../../../hooks/utility/useForm';
import {
  PolygraphyProductImageType,
  PolygraphyProductType,
  PolygraphyProductUrlType,
} from '../../../services/exports/Constants';
import Document from '../../pdf/Document';
import useFeedbackLabel from '../../../hooks/ui/useFeedbackLabel';
import { Utils } from '../../../services/helpers/Utils';
import FlyerForm from './templates/flyer/FlyerForm';
import StickerForm from './templates/sticker/StickerForm';
import DoorStickerForm from './templates/door-sticker/DoorStickerForm';

export default function ProductDetails({ type, template, data, onProductUpdated }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Marketing:Polygraphy:Products:ProductDetails' });

  const { company } = useSelector((store) => store.currentCompany);

  const { PolygraphyManager } = useApiClient();

  const { setErrorFeedback, resetFeedback, renderFeedbackLabel } = useFeedbackLabel();

  const { data: _data, setData, update } = useForm(data?.data);
  const [images, setImages] = useState(data?.images ?? []);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const urlTypeOptions = useMemo(
    () =>
      collect(Object.values(PolygraphyProductUrlType))
        .when(!company?.storefront_store_id, (collection) =>
          collection.reject((item) => item === PolygraphyProductUrlType.Storefront),
        )
        .map((item) => ({ label: t(`form.url_type.options.${item}`), value: item }))
        .toArray(),
    [company?.storefront_store_id, i18n.language],
  );

  useEffect(() => {
    !data && createDefault();
  }, []);

  async function createDefault() {
    setLoading(true);
    const { success, data: responseData } = await PolygraphyManager.createProduct(type, {
      template,
    });
    setLoading(false);

    if (!success) {
      return toast.error(t('toasts.failed_to_load_data'));
    }

    onProductUpdated(responseData);
    setData(responseData.data);
    setImages(responseData.images);
  }

  async function onSave(extra = {}) {
    resetFeedback();
    setSaving(true);
    const { success, data: responseData } = await PolygraphyManager.updateProduct(
      data.id,
      Utils.createFormData({
        data: { ..._data, ...extra },
        images: collect(images).whereNotIn('type', [PolygraphyProductImageType.QrCode]).toArray(),
        _method: 'PUT',
      }),
    );
    setSaving(false);

    if (!success) {
      setErrorFeedback(responseData);

      return toast.error(t('toasts.failed_to_save_changes'));
    }

    onProductUpdated(responseData);
    setData(responseData.data);
    setImages(responseData.images);

    return toast.success(t('toasts.changes_got_saved'));
  }

  const onImageChange = (type, file) =>
    setImages((current) => {
      const index = collect(current).search((item) => item.type === type);

      if (!file) {
        return collect(current)
          .when(index !== false, (collection) => collection.forget(index).values())
          .toArray();
      }

      return collect(current)
        .when(
          index !== false,
          (collection) =>
            collection.put(index, {
              ...current[index],
              file_url: file,
            }),
          (collection) =>
            collection.push({
              type,
              file_url: file,
            }),
        )
        .toArray();
    });

  const FormComponent =
    {
      [PolygraphyProductType.Flyer]: FlyerForm,
      [PolygraphyProductType.DoorSticker]: DoorStickerForm,
      [PolygraphyProductType.Sticker]: StickerForm,
    }[type] ?? null;

  const Preview = data?.file_url
    ? {
        [PolygraphyProductType.Flyer]: (
          <Document file={data.file_url} defaultScale={2} loading={loading} className="mt-[45px]" />
        ),
        [PolygraphyProductType.DoorSticker]: (
          <Document file={data.file_url} defaultScale={1} loading={loading} className="mt-[45px]" />
        ),
        [PolygraphyProductType.Sticker]: (
          <img src={data.file_url} alt="Sticker preview" className="mt-[45px] object-contain aspect-square" />
        ),
      }[type]
    : null;

  return (
    <Card className="mt-md">
      <Card.Grid hoverable={false} style={{ width: '50%' }}>
        {_data ? (
          <FormComponent
            template={template}
            data={_data}
            images={images}
            polygraphyProductUrlTypeOptions={urlTypeOptions}
            onChange={update}
            onImageChange={onImageChange}
            renderFeedbackLabel={renderFeedbackLabel}
            onSave={onSave}
            setData={setData}
          />
        ) : (
          <Skeleton active />
        )}
      </Card.Grid>
      <Card.Grid hoverable={false} style={{ width: '50%' }}>
        {data?.file_url ? (
          Preview
        ) : (
          <div className="flex h-full">
            <Empty className="m-auto" description={t('labels.save_changes_to_see_preview')} />
          </div>
        )}
        {Boolean(data?.id) && (
          <Flex gap="small" className="absolute top-md right-md">
            <Button icon={<DownloadOutlined />} onClick={() => window.open(data.file_url, '_blank')} />
            <Button loading={saving} onClick={() => onSave()} type="primary">
              {t('buttons.save_and_preview')}
            </Button>
          </Flex>
        )}
      </Card.Grid>
    </Card>
  );
}
